/**
 * Admin/support center: list, filter and triage all tickets (URS-F194).
 * Only accessible to roles with permission `support.edit` (admin by default).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Lifebuoy, ChatCircleDots, MagnifyingGlass, Funnel, ClockCounterClockwise, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  baja: 'bg-stone-50 text-stone-600 border-stone-200',
  media: 'bg-blue-50 text-blue-700 border-blue-200',
  alta: 'bg-amber-50 text-amber-700 border-amber-200',
  critica: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_COLORS = {
  abierto: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  en_curso: 'bg-blue-50 text-blue-700 border-blue-200',
  resuelto: 'bg-stone-100 text-stone-600 border-stone-200',
  cerrado: 'bg-stone-100 text-stone-400 border-stone-200',
};

export default function Support() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  // URS-F385: vista principal (abierto/en_curso) vs historial (resuelto/cerrado)
  const [viewMode, setViewMode] = useState('active');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterPriority !== 'all') params.set('priority', filterPriority);
      const [tk, st] = await Promise.all([
        axios.get(`${API_URL}/api/support/tickets?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/support/tickets/stats`, { withCredentials: true }),
      ]);
      setTickets(tk.data || []);
      setStats(st.data);
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
    setLoading(false);
  }, [filterStatus, filterPriority]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_URL}/api/users`, { withCredentials: true })
      .then(r => setUsers(r.data || []))
      .catch(() => {});
  }, []);

  const openDetail = async (ticketId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/support/tickets/${ticketId}`, { withCredentials: true });
      setActive(data);
    } catch { toast.error('Error'); }
  };

  const updateField = async (field, value) => {
    if (!active) return;
    try {
      const { data } = await axios.put(`${API_URL}/api/support/tickets/${active.ticket_id}`,
        { [field]: value }, { withCredentials: true });
      setActive(data);
      toast.success(lang === 'es' ? 'Actualizado' : 'Updated');
      load();
    } catch (e) { toast.error(errText(e, 'Error')); }
  };

  const postComment = async () => {
    if (!newComment.trim() || !active) return;
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/support/tickets/${active.ticket_id}/comments`,
        { text: newComment.trim() }, { withCredentials: true });
      setNewComment('');
      await openDetail(active.ticket_id);
      load();
    } catch { toast.error('Error'); }
    setPosting(false);
  };

  const visible = tickets.filter(t => {
    // URS-F385: separar la vista principal (activos) del Historial
    if (viewMode === 'active' && !['abierto', 'en_curso'].includes(t.status)) return false;
    if (viewMode === 'history' && !['resuelto', 'cerrado'].includes(t.status)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [t.ticket_id, t.title, t.description, t.created_by_name, t.created_by_email]
      .some(v => (v || '').toLowerCase().includes(s));
  });

  const switchView = (mode) => {
    setViewMode(mode);
    setFilterStatus('all'); // reset status filter para evitar combinaciones invalidas
  };

  const lb = lang === 'es' ? {
    title: 'Centro de soporte', subtitle: 'Tickets reportados por todos los usuarios. Triage y seguimiento centralizado.',
    search_ph: 'Buscar por titulo, ID, usuario…', filter_status: 'Estado', filter_priority: 'Prioridad', all: 'Todos',
    col_id: 'ID', col_title: 'Titulo', col_user: 'Usuario', col_priority: 'Prioridad', col_status: 'Estado', col_created: 'Creado', col_comments: '',
    empty: 'Sin tickets que mostrar.',
    status_label: 'Estado', priority_label: 'Prioridad', assigned_label: 'Asignado a', page_label: 'Pagina',
    conversation: 'Conversacion', no_comments: 'Sin comentarios todavia.', add_comment_ph: 'Responder al usuario…', comment_btn: 'Comentar', staff_badge: 'Soporte',
    history_btn: 'Historial', back_active_btn: 'Volver a activos',
    history_banner: 'Mostrando tickets Resueltos y Cerrados. Los tickets activos (Abierto / En curso) estan disponibles en la vista principal.',
  } : {
    title: 'Support center', subtitle: 'Tickets from all users. Triage and follow-up.',
    search_ph: 'Search by title, ID, user…', filter_status: 'Status', filter_priority: 'Priority', all: 'All',
    col_id: 'ID', col_title: 'Title', col_user: 'User', col_priority: 'Priority', col_status: 'Status', col_created: 'Created', col_comments: '',
    empty: 'No tickets to show.',
    status_label: 'Status', priority_label: 'Priority', assigned_label: 'Assigned to', page_label: 'Page',
    conversation: 'Conversation', no_comments: 'No comments yet.', add_comment_ph: 'Reply to user…', comment_btn: 'Comment', staff_badge: 'Support',
    history_btn: 'History', back_active_btn: 'Back to active',
    history_banner: 'Showing Resolved and Closed tickets. Active tickets (Open / In progress) are available in the main view.',
  };

  return (
    <div className="space-y-6" data-testid="support-admin-page">
      <div>
        <h1 className="text-2xl font-semibold text-stone-800 flex items-center gap-2"><Lifebuoy size={24} className="text-[#1f3e80]" weight="duotone" />{lb.title}</h1>
        <p className="text-sm text-stone-500 mt-0.5">{lb.subtitle}</p>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="support-stats">
          <div className="bg-white border border-stone-200 rounded-sm p-3">
            <p className="text-xs text-stone-500">Total</p>
            <p className="text-2xl font-semibold text-stone-800">{stats.total}</p>
          </div>
          {['abierto','en_curso','resuelto'].map(s => (
            <div key={s} className="bg-white border border-stone-200 rounded-sm p-3">
              <p className="text-xs text-stone-500 capitalize">{s.replace('_',' ')}</p>
              <p className="text-2xl font-semibold text-stone-800">{stats.by_status[s] || 0}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="support-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lb.search_ph} className="pl-9" />
        </div>
        <Funnel size={14} className="text-stone-400" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px]" data-testid="support-filter-status"><SelectValue placeholder={lb.filter_status} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lb.all}</SelectItem>
            {viewMode === 'active' ? (
              <>
                <SelectItem value="abierto">abierto</SelectItem>
                <SelectItem value="en_curso">en_curso</SelectItem>
              </>
            ) : (
              <>
                <SelectItem value="resuelto">resuelto</SelectItem>
                <SelectItem value="cerrado">cerrado</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px]" data-testid="support-filter-priority"><SelectValue placeholder={lb.filter_priority} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lb.all}</SelectItem>
            <SelectItem value="baja">baja</SelectItem>
            <SelectItem value="media">media</SelectItem>
            <SelectItem value="alta">alta</SelectItem>
            <SelectItem value="critica">critica</SelectItem>
          </SelectContent>
        </Select>
        {viewMode === 'active' ? (
          <Button
            data-testid="support-history-btn"
            variant="outline"
            size="sm"
            onClick={() => switchView('history')}
            className="ml-auto gap-1.5"
          >
            <ClockCounterClockwise size={14} weight="duotone" />
            {lb.history_btn}
          </Button>
        ) : (
          <Button
            data-testid="support-back-active-btn"
            variant="outline"
            size="sm"
            onClick={() => switchView('active')}
            className="ml-auto gap-1.5"
          >
            <ArrowLeft size={14} />
            {lb.back_active_btn}
          </Button>
        )}
      </div>

      {viewMode === 'history' && (
        <div className="text-xs text-stone-500 -mt-2" data-testid="support-history-banner">
          {lb.history_banner}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {loading ? (
          <p className="text-center text-sm text-stone-400 py-10">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-sm text-stone-400 py-10">{lb.empty}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{lb.col_id}</TableHead>
                <TableHead>{lb.col_title}</TableHead>
                <TableHead>{lb.col_user}</TableHead>
                <TableHead className="w-24">{lb.col_priority}</TableHead>
                <TableHead className="w-28">{lb.col_status}</TableHead>
                <TableHead className="w-28">{lb.col_created}</TableHead>
                <TableHead className="w-10">{lb.col_comments}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map(t => (
                <TableRow key={t.ticket_id} className="cursor-pointer hover:bg-stone-50" onClick={() => openDetail(t.ticket_id)} data-testid={`support-admin-row-${t.ticket_id}`}>
                  <TableCell className="font-mono text-xs text-stone-500">{t.ticket_id}</TableCell>
                  <TableCell className="font-medium text-stone-800">{t.title}</TableCell>
                  <TableCell className="text-xs text-stone-600">{t.created_by_name || t.created_by_email}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[t.status]}`}>{t.status.replace('_',' ')}</Badge></TableCell>
                  <TableCell className="text-xs text-stone-400">{new Date(t.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US')}</TableCell>
                  <TableCell>{t.comments_count > 0 && <span className="text-[10px] text-stone-400 flex items-center gap-0.5"><ChatCircleDots size={11} />{t.comments_count}</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(v) => { if (!v) { setActive(null); setNewComment(''); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="support-admin-detail">
          {active && (
            <>
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <DialogTitle className="text-base text-stone-800">{active.title}</DialogTitle>
                    <p className="text-xs text-stone-400 mt-0.5 font-mono">{active.ticket_id} · {active.created_by_name} ({active.created_by_email})</p>
                  </div>
                </div>
              </DialogHeader>
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <p className="text-[11px] text-stone-500">{lb.status_label}</p>
                    <Select value={active.status} onValueChange={(v) => updateField('status', v)}>
                      <SelectTrigger data-testid="support-admin-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="abierto">abierto</SelectItem>
                        <SelectItem value="en_curso">en_curso</SelectItem>
                        <SelectItem value="resuelto">resuelto</SelectItem>
                        <SelectItem value="cerrado">cerrado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] text-stone-500">{lb.priority_label}</p>
                    <Select value={active.priority} onValueChange={(v) => updateField('priority', v)}>
                      <SelectTrigger data-testid="support-admin-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baja">baja</SelectItem>
                        <SelectItem value="media">media</SelectItem>
                        <SelectItem value="alta">alta</SelectItem>
                        <SelectItem value="critica">critica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] text-stone-500">{lb.assigned_label}</p>
                    <Select value={active.assigned_to_user_id || '__none__'} onValueChange={(v) => updateField('assigned_to_user_id', v === '__none__' ? null : v)}>
                      <SelectTrigger data-testid="support-admin-assigned-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {users.filter(u => (u.roles || []).includes('admin')).map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-stone-50 border border-stone-100 rounded-sm p-3 text-sm whitespace-pre-wrap text-stone-700">{active.description}</div>
                {(() => {
                  const imgs = (active.screenshot_file_ids && active.screenshot_file_ids.length)
                    ? active.screenshot_file_ids
                    : (active.screenshot_file_id ? [active.screenshot_file_id] : []);
                  if (!imgs.length) return null;
                  return (
                    <div className="flex flex-wrap gap-2" data-testid="support-admin-detail-images">
                      {imgs.map((fid, i) => (
                        <a key={fid} href={`${API_URL}/api/files/download/${fid}`} target="_blank" rel="noreferrer" className="block">
                          <img src={`${API_URL}/api/files/download/${fid}`} alt={`screenshot-${i}`} className="rounded-sm border border-stone-200 max-h-72 object-contain" />
                        </a>
                      ))}
                    </div>
                  );
                })()}
                {active.current_url && <p className="text-[11px] text-stone-400">{lb.page_label}: <span className="font-mono">{active.current_url}</span></p>}

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-stone-600 flex items-center gap-1"><ChatCircleDots size={14} />{lb.conversation}</p>
                  {(active.comments || []).length === 0 ? (
                    <p className="text-xs text-stone-400 italic">{lb.no_comments}</p>
                  ) : (
                    <ul className="space-y-2">
                      {active.comments.map(c => (
                        <li key={c.comment_id} className={`p-2.5 rounded-sm text-sm border ${c.is_staff ? 'bg-blue-50 border-blue-100' : 'bg-stone-50 border-stone-100'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold ${c.is_staff ? 'text-blue-700' : 'text-stone-700'}`}>{c.name}</span>
                            {c.is_staff && <Badge variant="outline" className="text-[10px] bg-blue-100 border-blue-200 text-blue-800">{lb.staff_badge}</Badge>}
                            <span className="text-[10px] text-stone-400 ml-auto">{new Date(c.timestamp).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}</span>
                          </div>
                          <p className="whitespace-pre-wrap text-stone-700">{c.text}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Textarea data-testid="support-admin-comment-input" rows={2} value={newComment}
                    onChange={(e) => setNewComment(e.target.value)} placeholder={lb.add_comment_ph} />
                  <Button onClick={postComment} disabled={posting || !newComment.trim()}
                    className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm" size="sm" data-testid="support-admin-comment-submit">
                    {posting ? '...' : lb.comment_btn}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
